import { FormEvent, useEffect, useState } from 'react';
import { formIssues, issueFor, ValidationIssue } from '../../services/deliveryConfigApi';
import { isVersionConflict, promotionsApi, TERMS_LOCKED_MESSAGE, toPromoIssues, VERSION_CONFLICT_MESSAGE } from '../../services/promotionsApi';
import { PromoCodeDetail } from '../../types/promotions';
import { describedBy, ExpiryField, FormField, RadioGroup } from './PromoFormParts';
import { initialValues, PromoFormValues, toCreateRequest, toUpdateRequest, validatePromo } from './promoFormModel';

interface PromoCodeFormProps {
  /** null = create; otherwise the loaded code to edit. */
  promo: PromoCodeDetail | null;
  token: string;
  onClose: () => void;
  onSaved: (saved: PromoCodeDetail) => void;
  /** BR-10 — the "Reload" action on a version conflict. */
  onReload?: () => void;
}

/**
 * Spec 059 Flow A / B — create or edit a promo code. Once anyone holds the code (`termsLocked`),
 * type, value, max per use and start are disabled (BR-8); limits can only go up (BR-9).
 */
export default function PromoCodeForm({ promo, token, onClose, onSaved, onReload }: PromoCodeFormProps) {
  const editing = promo !== null;
  const locked = !!promo?.termsLocked;
  const [v, setV] = useState<PromoFormValues>(() => initialValues(promo));
  const [cap, setCap] = useState<number | null>(null);
  const [issues, setIssues] = useState<ValidationIssue[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [saving, setSaving] = useState(false);

  // The cap lives on the finance-gated settings path; a promotions-only admin gets 403 and simply
  // sees no hint — the server enforces BR-3 either way.
  useEffect(() => {
    let live = true;
    promotionsApi.getCap(token).then((s) => { if (live) setCap(s.maxDiscountPercent); }).catch(() => {});
    return () => { live = false; };
  }, [token]);

  const set = <K extends keyof PromoFormValues>(key: K, value: PromoFormValues[K]) => setV((prev) => ({ ...prev, [key]: value }));
  const err = (field: string) => issueFor(issues, field);
  const busy = saving;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setError(null);
    setConflict(false);
    const local = validatePromo(v, { original: promo, capPercent: cap });
    if (local.length > 0) { setIssues(local); return; }
    setIssues([]);
    setSaving(true);
    try {
      const saved = promo
        ? await promotionsApi.update(promo.id, toUpdateRequest(v, promo), token)
        : await promotionsApi.create(toCreateRequest(v), token);
      onSaved(saved);
    } catch (ex) {
      if (isVersionConflict(ex)) { setConflict(true); return; }
      const parsed = toPromoIssues(ex);
      if (parsed.length > 0) setIssues(parsed);
      else setError(ex instanceof Error ? ex.message : 'Failed to save the code');
    } finally {
      setSaving(false);
    }
  };

  const formLevel = formIssues(issues);
  const percent = v.discountType === 'PERCENT';
  const termsDisabled = busy || locked;

  return (
    <div className="admin-modal-overlay">
      <div className="admin-modal" role="dialog" aria-modal="true" aria-label={editing ? `Edit ${promo.code}` : 'New promo code'} style={{ maxWidth: '600px', width: '100%' }}>
        <div className="admin-modal-header">
          <h3 style={{ margin: 0 }}>{editing ? `Edit ${promo.code}` : 'New promo code'}</h3>
          <button type="button" className="admin-modal-close" onClick={onClose} disabled={busy} aria-label="Close">&times;</button>
        </div>
        <form onSubmit={submit} noValidate>
          <div className="admin-modal-body">
            {conflict && (
              <div className="admin-info-banner danger" role="alert" data-testid="promo-conflict">
                {VERSION_CONFLICT_MESSAGE}{' '}
                {onReload && <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={onReload}>Reload</button>}
              </div>
            )}
            {error && <div className="admin-info-banner danger" role="alert" data-testid="promo-form-error">{error}</div>}
            {formLevel.length > 0 && <div className="admin-info-banner danger" role="alert" data-testid="promo-form-issues">{formLevel.join(' ')}</div>}

            <FormField id="promo-code" label="Code" error={err('code')} hint={editing ? 'Codes never change once created.' : '4–20 letters or digits; stored in capitals.'}>
              <input id="promo-code" className="admin-form-input" value={v.code} disabled={busy || editing} maxLength={20}
                {...describedBy('promo-code', err('code'), true)} onChange={(e) => set('code', e.target.value.toUpperCase())} />
            </FormField>
            <FormField id="promo-name" label="Internal name" error={err('name')}>
              <input id="promo-name" className="admin-form-input" value={v.name} disabled={busy} maxLength={80}
                {...describedBy('promo-name', err('name'))} onChange={(e) => set('name', e.target.value)} />
            </FormField>
            <RadioGroup name="promo-audience" label="Audience" value={v.audience} disabled={busy} onChange={(a) => set('audience', a)}
              options={[{ value: 'PUBLIC', label: 'Public — anyone with the code or link' }, { value: 'INVITE_ONLY', label: 'Invite-only — people you share it with' }]} />

            {locked && <div className="admin-info-banner info" role="note" data-testid="terms-locked-hint">{TERMS_LOCKED_MESSAGE}</div>}
            <RadioGroup name="promo-type" label="Discount type" value={v.discountType} disabled={termsDisabled} onChange={(t) => set('discountType', t)}
              options={[{ value: 'PERCENT', label: 'Percentage' }, { value: 'FIXED', label: 'Fixed AED' }]} />
            <FormField id="promo-value" label={percent ? 'Value (%)' : 'Value (AED)'} error={err('discountValue')}
              hint={percent && cap !== null ? `Max allowed: ${cap}%` : !percent && cap !== null ? `Still limited to ${cap}% of the service price.` : undefined}>
              <input id="promo-value" type="number" inputMode="decimal" step={percent ? 1 : 0.01} min={1} className="admin-form-input" value={v.discountValue}
                disabled={termsDisabled} {...describedBy('promo-value', err('discountValue'), cap !== null)} onChange={(e) => set('discountValue', e.target.value)} />
            </FormField>
            {percent && (
              <FormField id="promo-max-per-use" label="Max AED per use (optional)" error={err('maxDiscountPerUse')}>
                <input id="promo-max-per-use" type="number" inputMode="decimal" step={0.01} min={1} className="admin-form-input" value={v.maxDiscountPerUse}
                  disabled={termsDisabled} {...describedBy('promo-max-per-use', err('maxDiscountPerUse'))} onChange={(e) => set('maxDiscountPerUse', e.target.value)} />
              </FormField>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(12rem, 1fr))', gap: '0 1rem' }}>
              <FormField id="promo-uses" label="Uses per customer" error={err('usesPerHolder')}>
                <input id="promo-uses" type="number" step={1} min={1} max={100} className="admin-form-input" value={v.usesPerHolder}
                  disabled={busy} {...describedBy('promo-uses', err('usesPerHolder'))} onChange={(e) => set('usesPerHolder', e.target.value)} />
              </FormField>
              <FormField id="promo-holders" label="Number of users" error={err('maxHolders')}>
                <input id="promo-holders" type="number" step={1} min={1} max={100000} className="admin-form-input" value={v.maxHolders}
                  disabled={busy} {...describedBy('promo-holders', err('maxHolders'))} onChange={(e) => set('maxHolders', e.target.value)} />
              </FormField>
            </div>
            <FormField id="promo-starts" label="Start (Asia/Dubai)" error={err('startsAt')} hint={editing ? undefined : 'Leave blank to start now.'}>
              <input id="promo-starts" type="datetime-local" className="admin-form-input" style={{ maxWidth: '14rem' }} value={v.startsAt}
                disabled={termsDisabled} {...describedBy('promo-starts', err('startsAt'), !editing)} onChange={(e) => set('startsAt', e.target.value)} />
            </FormField>
            <ExpiryField startsAt={v.startsAt} expiresAt={v.expiresAt} noExpiry={v.noExpiry} disabled={busy} error={err('expiresAt')}
              onChange={(next) => setV((prev) => ({ ...prev, ...next }))} />
          </div>
          <div className="admin-modal-footer">
            <button type="button" className="admin-btn admin-btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" className="admin-btn admin-btn-primary" disabled={busy}>
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Create code'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
